// Catalog of insurance categories and contract types.
// Source: for-ai/Vertragskategorien u. Vertragsarten- mo.xlsx (sheet "Neu").

// 8 abstract risk-object categories (the dimension we render as columns).
// Mirrors the categories used in ContactRisksPanel so terminology is consistent.
export type RiskCategoryKey =
  | "betrieb"
  | "immo"
  | "kfz"
  | "haftung"
  | "umwelt"
  | "transport"
  | "menschen"
  | "liquid";

export type RiskCategoryDef = {
  key: RiskCategoryKey;
  label: string;
  short: string;
  // matches a backend risk-object's `riskType` string to this category.
  match: (riskType: string | undefined) => boolean;
};

const norm = (s: string | undefined) => (s ?? "").toLowerCase();

export const RISK_CATEGORIES: RiskCategoryDef[] = [
  {
    key: "betrieb",
    label: "Betriebs- & Anlagevermögen",
    short: "Betrieb",
    match: (t) => {
      const n = norm(t);
      if (!n) return false;
      if (n.includes("immo")) return false;
      if (n.includes("kfz")) return false;
      if (n.includes("transport")) return false;
      if (n.includes("kennzahl")) return false;
      return (
        n.includes("sachwert") ||
        n.includes("anlage") ||
        n.includes("betrieb") ||
        n.includes("maschine") ||
        n.includes("inhalt") ||
        n.includes("elektron")
      );
    },
  },
  {
    key: "immo",
    label: "Immobilienprojekte",
    short: "Immo",
    match: (t) => norm(t).includes("immo"),
  },
  {
    key: "kfz",
    label: "KFZ / Fahrzeuge",
    short: "KFZ",
    match: (t) => norm(t).includes("kfz") || norm(t).includes("fahrzeug"),
  },
  {
    key: "haftung",
    label: "Haftung & Cyber",
    short: "Haftung",
    match: (t) => {
      const n = norm(t);
      return (
        n.includes("haftung") ||
        n.includes("recht") ||
        n.includes("cyber") ||
        n.includes("d&o")
      ) && !n.includes("umwelt");
    },
  },
  {
    key: "umwelt",
    label: "Umwelt",
    short: "Umwelt",
    match: (t) => norm(t).includes("umwelt"),
  },
  {
    key: "transport",
    label: "Transport & Waren",
    short: "Transport",
    match: (t) => norm(t).includes("transport") || norm(t).includes("ware"),
  },
  {
    key: "menschen",
    label: "Menschen / Personal",
    short: "Menschen",
    match: (t) => {
      const n = norm(t);
      return (
        n.includes("mensch") ||
        n.includes("personal") ||
        n.includes("hr") ||
        n.includes("mitarbeiter")
      );
    },
  },
  {
    key: "liquid",
    label: "Liquidität & Finanzen",
    short: "Liquidität",
    match: (t) => {
      const n = norm(t);
      return (
        n.includes("liquid") ||
        n.includes("ertrag") ||
        n.includes("finanz") ||
        n.includes("kennzahl") ||
        n.includes("kredit") ||
        n.includes("burgschaft") ||
        n.includes("bürgschaft")
      );
    },
  },
];

export const findRiskCategory = (key: RiskCategoryKey): RiskCategoryDef | undefined =>
  RISK_CATEGORIES.find((c) => c.key === key);

export const matchRiskCategoryFromRiskType = (
  riskType: string | undefined,
): RiskCategoryKey | undefined => {
  for (const c of RISK_CATEGORIES) {
    if (c.match(riskType)) return c.key;
  }
  return undefined;
};

// ---- Catalog model -------------------------------------------------------

export type Vertragsart = {
  id: string;
  name: string;
  detail?: string;
  defaultDeckung?: string;
  // Which risk-object categories this Vertragsart applies to.
  // Most have one; some span two (e.g. KFZ touches Betrieb + Haftung).
  riskCategories: RiskCategoryKey[];
};

export type Vertragskategorie = {
  id: string;
  name: string;
  arten: Vertragsart[];
};

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

type ArtSpec = {
  name: string;
  detail?: string;
  deckung?: string;
  riskCategories: RiskCategoryKey[];
};

const make = (cat: string, arten: ArtSpec[]): Vertragskategorie => ({
  id: slug(cat),
  name: cat,
  arten: arten.map((a) => ({
    id: slug(`${cat}-${a.name}-${a.detail ?? ""}`),
    name: a.name.trim(),
    detail: a.detail?.trim(),
    defaultDeckung: a.deckung?.trim(),
    riskCategories: a.riskCategories,
  })),
});

const a = (
  name: string,
  riskCategories: RiskCategoryKey[],
  detail?: string,
  deckung?: string,
): ArtSpec => ({ name, detail, deckung, riskCategories });

export const VERTRAGSKATALOG: Vertragskategorie[] = [
  make("Sachversicherungen fürs Unternehmen", [
    a(
      "Gebäudeversicherung (nur Variante 1)",
      ["immo"],
      undefined,
      "Die Gebäudeversicherung versichert private und gewerbliche Immobilien gegen Substanzschäden an Gebäuden, Gebäudezubehör und sonstigen Grundstücksbestandteilen.",
    ),
    a("Inhaltsversicherung", ["betrieb"]),
    a("Maschinenversicherung (stationär)", ["betrieb"]),
    a("Maschinenversicherung (mobil)", ["betrieb"]),
    a("Gap-Deckung f. Maschinen", ["betrieb"]),
    a("Elektronikversicherung", ["betrieb"]),
    a("Glas- u. Außenwerbungsvers.", ["betrieb", "immo"]),
    a("Photovoltaikversicherung", ["betrieb"]),
    a("Schlüsselverlustversicherung", ["betrieb"]),
    a("Waren-/Vorratsversicherung", ["betrieb"]),
  ]),
  make("Ertragsausfall fürs Unternehmen", [
    a("Ertragsausfall-Sach", ["liquid"]),
    a("Ertragsausfall-Elektronik-BU", ["liquid"]),
    a("Ertragsausfall-Maschinen-BU", ["liquid"]),
    a("Seuchen-/Betriebsschließungsversicherung", ["liquid"]),
    a("Ertragsausfall-Energieversorgung", ["liquid"]),
  ]),
  make("Haftpflichtversicherungen für Unternehmen", [
    a(
      "Betriebs-, Umwelt-Haftpflicht- u. Umweltschadens-Versicherung",
      ["haftung", "umwelt"],
      "eventuell Produkt etc. mit Bausteinen",
    ),
    a("Betriebshaftpflichtversicherung (Einzelvertrag)", ["haftung"]),
    a(
      "Umwelthaftpflicht- und Umweltschadens Versicherung (Einzelvertrag)",
      ["umwelt"],
      "Bausteine",
    ),
    a("Vermögensschadenhaftpflichtversicherung", ["haftung"]),
    a("Veranstalter-Haftpflichtversicherung", ["haftung"]),
    a("Gewässerschadenhaftpflichtversicherung", ["umwelt", "haftung"]),
    a("Berufshaftpflichtversicherung", ["haftung"]),
  ]),
  make("Transportversicherungen", [
    a("Warentransportversicherung", ["transport"]),
    a("Fahrzeuginhaltsversicherung (Werkverkehr)", ["transport", "kfz"]),
    a("Frachtführerhaftpflicht", ["transport", "haftung"]),
    a("Messeversicherung", ["transport"]),
  ]),
  make("Rechtsschutzversicherungen", [
    a("Firmen-Rechtsschutzversicherung", ["haftung"], "diverse Bausteine"),
    a("Erweiterte Straf-Rechtsschutzversicherung", ["haftung"]),
    a("Urheber-Rechtsschutzversicherung", ["haftung"]),
    a("D&O Deckungsklage-Rechtsschutzversicherung", ["haftung"]),
    a("Privat-Rechtsschutzversicherung", ["haftung"]),
    a("Vermieter-Rechtsschutz", ["haftung", "immo"]),
    a("Mietausfall-/Räumungsschutz-Rechtsschutz", ["haftung", "immo"]),
    a("Bauherren-/Sanierungsrechtsschutz", ["haftung", "immo"]),
  ]),
  make("KFZ-Versicherungen", [
    a(
      "KFZ-Versicherung",
      ["kfz"],
      "Baustein nur Haft, Baustein Haft und TK, Baustein Haft VK / TK",
    ),
    a("Dienstreisekasko", ["kfz"]),
    a("Handel- u. Handwerksdeckung", ["kfz"]),
    a("Leasing-Rückläufer-Versicherung", ["kfz"]),
  ]),
  make("Spezialversicherungen", [
    a("Cyberversicherung", ["haftung"]),
    a("Warenkreditversicherung", ["liquid"]),
    a("Vertrauensschadenversicherung", ["liquid"]),
    a("D&O", ["haftung", "menschen"]),
    a("Vermögens-Eigenschadenversicherung", ["liquid"]),
    a("Insolvenzanfechtungsversicherung", ["liquid"]),
    a("Automatenversicherung", ["betrieb"]),
    a("Ausstellungsversicherung", ["betrieb"]),
    a("Umsatzpolice", ["liquid"]),
  ]),
  make("Top-Jobber Versicherungen (einschl. Leitende)", [
    a("Dread-Disease Versicherung", ["menschen"]),
    a("Keyman-Versicherung", ["menschen"]),
    a("Vermögensschadenhaftpflicht f. (Leitende) Angestellte", ["haftung", "menschen"]),
    a("Strafrechtsschutz-Versicherung", ["haftung", "menschen"]),
    a("Anstellungsvertrags-Rechtsschutzversicherung", ["haftung", "menschen"]),
    a("Vermögensschaden-Rechtsschutzversicherung", ["haftung", "menschen"]),
    a("D&O Selbstbeteiligungsabsicherung", ["haftung", "menschen"]),
    a("D&O Vertrags-Rechtsschutzversicherung", ["haftung", "menschen"]),
  ]),
  make("Menschen im Unternehmen", [
    a("Unfallversicherung", ["menschen"]),
    a("bAV - Pensionszusage", ["menschen"]),
    a("bAV - Arbeitgeberfinanziert (außer Pensionszusage)", ["menschen"]),
    a("bAV - Arbeitnehmerfinanziert (Entgeltumwandlung)", ["menschen"]),
    a("bKV (betriebl. Krankenversicherung)", ["menschen"]),
    a("Zeitwertkonten", ["menschen"]),
    a("Sachbezug oder Firmenkonsumkarte", ["menschen"]),
    a("Krankenversicherung f. ins Ausland entsandte Mitarbeiter", ["menschen"]),
    a("Betriebliche Berufsunfähigkeits- u. Pflegeversicherung", ["menschen"]),
    a("Betriebliche Einkommensabsicherung", ["menschen"]),
    a("Betriebliche Arbeitsunfähigkeitsversicherung", ["menschen"]),
    a("Lohnfortzahlungsversicherung", ["menschen"]),
    a("Geschäftsführer- und Inhaber-Versorgung", ["menschen"]),
  ]),
  make("Liquidität", [
    a("Factoring", ["liquid"]),
    a("Leasing", ["liquid"]),
    a("Mietkauf", ["liquid"]),
    a("Debitoren- und Abrechnungsmanagment", ["liquid"]),
    a("Kautionsversicherung, Bürgschaftsversicherung", ["liquid"]),
    a("Investitionsgüter-Kreditversicherung", ["liquid"]),
    a("Lohnoptimierung", ["liquid"]),
  ]),
  make("Immobilienprojekte", [
    a(
      "Gebäudeversicherung (alle Varianten)",
      ["immo"],
      undefined,
      "Die Gebäudeversicherung versichert private und gewerbliche Immobilien gegen Substanzschäden an Gebäuden, Gebäudezubehör und sonstigen Grundstücksbestandteilen.",
    ),
    a("Photovoltaikversicherung", ["immo"]),
    a("Bauleistungsversicherung", ["immo"]),
    a("Bauherren-Haftpflichtversicherung", ["immo", "haftung"]),
    a("Feuerrohbauversicherung", ["immo"]),
    a("Glas- u. Außenwerbungsvers.", ["immo", "betrieb"]),
    a("Haus - und Grundbesitzerhaftpflichtversicherung", ["immo", "haftung"]),
    a("Vermieter-Rechtsschutz", ["immo", "haftung"]),
    a("Mietausfall-/Räumungsschutz-Rechtsschutz", ["immo", "haftung"]),
    a("Bauherren-/Sanierungsrechtsschutz", ["immo", "haftung"]),
  ]),
  make("PK-Vertragsarten", [
    a("Bausparvertrag", ["liquid"]),
    a("Lebensversicherung (Direktversicherung)", ["menschen"]),
    a("Rentenversicherung (Direktversicherung)", ["menschen"]),
    a("Darlehen (endfälliges)", ["liquid"]),
    a("Lebensversicherung (fondsgebundene o. Teilauszahlung)", ["menschen"]),
    a("Rentenversicherung (fondsgebundene o. Teilauszahlung)", ["menschen"]),
    a("Hausratversicherung", ["betrieb"]),
    a("Investmentfonds", ["liquid"]),
    a("Lebensversicherung (kapitalbildende o. Teilauszahlung)", ["menschen"]),
    a("Risiko Lebensversicherung", ["menschen"]),
    a("Sonstiges", ["liquid"]),
    a("Darlehen (sonstiges)", ["liquid"]),
    a("Aktienfonds", ["liquid"]),
    a("Darlehen (Annuitäten)", ["liquid"]),
    a("Ausbildungs-Versicherung", ["menschen"]),
    a("Darlehen (Bausparen)", ["liquid"]),
    a("Bausparen (VL)", ["liquid"]),
    a("Finanzierung (bausparunterlegte)", ["liquid"]),
    a("Bausparen (klassisch)", ["liquid"]),
    a("Altersvorsorge", ["menschen"]),
    a("Festgeld", ["liquid"]),
    a("Festverzinsliche Wertpapiere", ["liquid"]),
    a("Flexplan", ["liquid"]),
    a("Lebensversicherung (fondsgebundene m. Teilauszahlung)", ["menschen"]),
    a("Rentenversicherung (fondsgebundene m. Teilauszahlung)", ["menschen"]),
    a("Geldmarktfonds", ["liquid"]),
    a("Gesetzliche Rentenversicherung", ["menschen"]),
    a("Girokonto", ["liquid"]),
    a("Goldsparplan", ["liquid"]),
    a("Privat-Haftpflichtversicherung", ["haftung"]),
    a("Haus - und Grundbesitzerhaftpflicht", ["immo", "haftung"]),
    a("Immobiliendarlehen", ["liquid", "immo"]),
    a("Immobilienfinanzierung", ["liquid", "immo"]),
    a("Lebensversicherung (kapitalbildende m. Teilauszahlung)", ["menschen"]),
    a("KFZ-Finanzierung", ["liquid", "kfz"]),
    a("Kinderinvaliditätsversicherung", ["menschen"]),
    a("Konsumfinanzierung", ["liquid"]),
    a("Kontokorrent", ["liquid"]),
    a("Kontoüberziehung", ["liquid"]),
    a("Private Krankenversicherung", ["menschen"]),
    a("Kreditkarte", ["liquid"]),
    a("Lebensversicherung (VL)", ["menschen"]),
    a("LV-Darlehen", ["liquid"]),
    a("Mischfonds", ["liquid"]),
    a("Offener Immobilienfonds", ["liquid", "immo"]),
    a("P.U.R. KONZEPT", ["liquid"]),
    a("Parkdepot Flex", ["liquid"]),
    a("Passiveinlagen", ["liquid"]),
    a("Privatdarlehen", ["liquid"]),
    a("Private Basisversorgung - Rürup-Rente", ["menschen"]),
    a("Private Pflegerente", ["menschen"]),
    a("Privathaftpflichtversicherung", ["haftung"]),
    a("Ratenkredit", ["liquid"]),
    a("Reisegepäckversicherung", ["betrieb"]),
    a("Rentenfonds", ["liquid"]),
    a("Private Rentenversicherung", ["menschen"]),
    a("Riester-Fonds", ["menschen"]),
    a("Riester-Rente", ["menschen"]),
    a("Schutzbrief", ["kfz"]),
    a("Berufsunfähigkeitsversicherung", ["menschen"]),
    a("Sonstige Verpflichtungen", ["liquid"]),
    a("Sonstiger Fonds", ["liquid"]),
    a("Sparbriefe", ["liquid"]),
    a("Sparbuch", ["liquid"]),
    a("Tagesgeldkonto", ["liquid"]),
    a("Tierhalterhaftpflichtversicherung", ["haftung"]),
    a("Unterhalt", ["liquid"]),
    a("Unterstützungskasse", ["menschen"]),
    a("VASP", ["liquid"]),
    a("Vermögens- und Einkommensplan", ["liquid"]),
    a("VL-Depot", ["liquid"]),
    a("Vorausdarlehen", ["liquid"]),
    a("Wassersporthaftpflichtversicherung", ["haftung"]),
    a("Wertpapiersparen VL", ["liquid"]),
    a("Wohnriester", ["menschen", "immo"]),
    a("Zwischenfinanzierung", ["liquid"]),
  ]),
];

// Status taxonomy ----------------------------------------------------------

export type StatusValue =
  | "bestehend"
  | "nicht_bestehend"
  | "angebotsphase"
  | "in_pruefung"
  | "nicht_erforderlich"
  | "nicht_gewuenscht";

export const STATUS_LABEL: Record<StatusValue, string> = {
  bestehend: "Bestehend",
  nicht_bestehend: "Nicht bestehend",
  angebotsphase: "Angebotsphase",
  in_pruefung: "In Prüfung",
  nicht_erforderlich: "Nicht erforderlich",
  nicht_gewuenscht: "Nicht gewünscht",
};

export const MANUAL_STATUS: StatusValue[] = ["nicht_erforderlich", "nicht_gewuenscht"];

// Map a contract's branch/zweig field to a Vertragsart id (best-effort).
export const matchVertragsartFromBranch = (
  branch: string | null | undefined,
): string | undefined => {
  if (!branch) return undefined;
  const needle = branch.toLowerCase().trim();
  for (const k of VERTRAGSKATALOG) {
    for (const art of k.arten) {
      const hay = `${art.name} ${art.detail ?? ""}`.toLowerCase();
      if (hay.includes(needle) || needle.includes(art.name.toLowerCase())) {
        return art.id;
      }
    }
  }
  return undefined;
};

export const findVertragsart = (
  id: string,
): { kat: Vertragskategorie; art: Vertragsart } | undefined => {
  for (const k of VERTRAGSKATALOG) {
    const art = k.arten.find((x) => x.id === id);
    if (art) return { kat: k, art };
  }
  return undefined;
};
